import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import {
  SubscriptionFormRule,
  SubscriptionFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultSubscription } from "@factory/form/form-default-subscription.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateSubscription,
  useQyGetSubscriptionById,
  useQyDeleteSubscription,
} from "@factory/query/subscription.query";
import { GetResponseSubscriptionModel } from "@factory/model/query-model-subscription";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditSubscription() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultSubscription();
  const navigate = useNavigate();
  const { mapUpdateSubscription, mapDeleteSubscription } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { subscriptionId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Subscription
  const handleGetApiSuccess = (data: GetResponseSubscriptionModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    // setValue("tenant", responseData?.tenant || "");
    setValue("member", responseData?.member || "");
    setValue("service", responseData?.service || "");
    setValue("startDate", responseData?.start_date || "");
    setValue("endDate", responseData?.end_date || "");
    setValue("amount", responseData?.amount);
    setValue("isSubscription", Boolean(responseData?.is_subscription));
  };
  const {
    data: subscriptionResponse,
    isLoading,
    isError: subscriptionError,
  } = useQyGetSubscriptionById(subscriptionId || "", handleGetApiSuccess);
  const subscriptionData = subscriptionResponse?.data?.[0];

  // API UPDATE Subscription
  const handleUpdateApiSuccess = () => {
    showSuccess("Subscription updated successfully");
  };
  const { mutate: updateSubscription, isLoading: isEditLoading } =
    useQyUpdateSubscription(handleUpdateApiSuccess);

  // API DELETE Subscription
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Subscription deleted successfully");
  };
  const { mutate: deleteSubscription } = useQyDeleteSubscription(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<SubscriptionFormSchema>({
    defaultValues: getDefault(subscriptionData),
    resolver: zodResolver(SubscriptionFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: SubscriptionFormSchema) => {
    if (!subscriptionData || !subscriptionData.code) {
      throw new Error("No subscription data");
    }

    const formData = mapUpdateSubscription(form, subscriptionData.code);
    updateSubscription(formData);
  };
  const handleValidateError = (err: FieldErrors<SubscriptionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!subscriptionData || !subscriptionData.code) {
      throw new Error("No subscription data found");
    }

    const form = mapDeleteSubscription(subscriptionData.code);
    deleteSubscription(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    subscriptionId,
    subscriptionData,
    isLoading,
    subscriptionError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
